import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Package, Search, Filter, Plus, Edit, MapPin, Calendar, AlertTriangle,
  CheckCircle, Clock, BarChart3, TrendingUp, Shield, Eye, Award
} from 'lucide-react';
import { salesService } from '../services/salesService';
import { InventoryItem, InventoryFilters } from '../types';
import { PesoComplianceDialog } from '@/components/inventory/batches/PesoComplianceDialog';
import { UpdateQualityDialog } from '@/components/inventory/batches/UpdateQualityDialog';
import { ExpiringBatchesWidget, type ExpiringBatchSummary } from '@/components/inventory/batches/ExpiringBatchesWidget';
import { ExpiringBatchesAlertTab, type ExpiringBatch } from '@/components/inventory/batches/ExpiringBatchesAlertTab';
import type { BatchResponse } from '@/types/inventory';
import { isPesoCertificateExpiringSoon } from '@/types/inventory';
import { BatchFiltersPanel } from '@/components/inventory/batches/BatchFiltersPanel';
import type { BatchFilters } from '@/types/availableBatches';

interface InventoryCardProps {
  item: InventoryItem;
  onUpdateQuality?: (item: InventoryItem) => void;
  onMarkPesoCompliant?: (item: InventoryItem) => void;
}

const InventoryCard: React.FC<InventoryCardProps> = ({ item, onUpdateQuality, onMarkPesoCompliant }) => {
  const [showViewDialog, setShowViewDialog] = useState(false);

  const availabilityPercentage = (item.availableQuantity / item.quantity) * 100;
  const isLowStock = availabilityPercentage < 20;
  const isPesoExpiring = item.pesoCompliance?.expiryDate ?
    isPesoCertificateExpiringSoon(item.pesoCompliance.expiryDate.toISOString()) : false;
  const isAllocated = item.availableQuantity < item.quantity;
  const hasPesoCertificate = item.pesoCompliance?.status === 'valid' || item.pesoCompliance?.status === 'expired';

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <CardTitle className="text-lg">{item.batchNumber}</CardTitle>
            <div className="flex items-center space-x-2">
              <Badge className={getStatusColor(item.status)}>{item.status}</Badge>
              <Badge variant="outline">{item.gasType}</Badge>
              {isPesoExpiring && <Badge className="bg-orange-100 text-orange-800">PESO Expiring</Badge>}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <div className="text-gray-600">Available</div>
            <div className="font-semibold">{item.availableQuantity.toLocaleString()} {item.unit}</div>
          </div>
          <div>
            <div className="text-gray-600">Total</div>
            <div className="font-semibold">{item.quantity.toLocaleString()} {item.unit}</div>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span>Availability</span>
            <span className={isLowStock ? 'text-red-600' : 'text-green-600'}>
              {availabilityPercentage.toFixed(1)}%
            </span>
          </div>
          <Progress value={availabilityPercentage} className="h-2" />
        </div>

        <div className="space-y-2 text-sm">
          <div className="flex items-center text-gray-600">
            <Shield className="w-4 h-4 mr-2" />
            PESO: {item.pesoCompliance.status} (Exp: {new Date(item.pesoCompliance.expiryDate).toLocaleDateString()})
          </div>
          <div className="flex items-center text-gray-600">
            <MapPin className="w-4 h-4 mr-2" />
            {item.location.plantId} - {item.location.tankId}
          </div>
          <div className="flex items-center text-gray-600">
            <Calendar className="w-4 h-4 mr-2" />
            Produced: {new Date(item.productionDate).toLocaleDateString()}
          </div>
        </div>

        <div className="pt-2 border-t">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Value</span>
            <span className="font-semibold">₹{(item.availableQuantity * item.pricePerUnit).toLocaleString()}</span>
          </div>
        </div>

        {/* US-BATCH-003 & US-BATCH-004: Action Buttons */}
        <div className="pt-3 space-y-2">
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1 flex items-center justify-center gap-2"
              onClick={() => setShowViewDialog(true)}
            >
              <Eye className="w-4 h-4" />
              View Details
            </Button>
            <Button
              variant="default"
              size="sm"
              className="flex-1 flex items-center justify-center gap-2"
              onClick={() => onUpdateQuality?.(item)}
            >
              <Shield className="w-4 h-4" />
              Update Quality
            </Button>
          </div>
          {/* US-BATCH-004: Mark PESO Compliant Button */}
          <Button
            variant={hasPesoCertificate ? "outline" : "default"}
            size="sm"
            className="w-full flex items-center justify-center gap-2"
            onClick={() => onMarkPesoCompliant?.(item)}
            title={
              hasPesoCertificate
                ? "Update PESO certificate"
                : "Mark as PESO compliant"
            }
          >
            <Award className="w-4 h-4" />
            {hasPesoCertificate ? "Update PESO" : "Mark PESO Compliant"}
          </Button>
          {isPesoExpiring && hasPesoCertificate && (
            <div className="text-xs text-orange-600 flex items-center gap-1 px-2">
              <AlertTriangle className="w-3 h-3" />
              Certificate expires soon
            </div>
          )}
        </div>

        {/* View Details Dialog */}
        <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Package className="w-5 h-5" />
                Batch Details: {item.batchNumber}
              </DialogTitle>
              <DialogDescription>Complete information for this batch</DialogDescription>
            </DialogHeader>

            <div className="space-y-6">
              {/* Status Section */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-600">Status</label>
                  <Badge className={`mt-1 w-fit ${getStatusColor(item.status)}`}>{item.status}</Badge>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Gas Type</label>
                  <Badge variant="outline" className="mt-1 w-fit">{item.gasType}</Badge>
                </div>
              </div>

              {/* Quantity Section */}
              <div className="border-t pt-4">
                <h3 className="font-semibold mb-3">Quantity Information</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <div className="text-sm text-gray-600">Total Quantity</div>
                    <div className="font-semibold text-lg">{item.quantity.toLocaleString()} {item.unit}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Available</div>
                    <div className="font-semibold text-lg text-green-600">{item.availableQuantity.toLocaleString()} {item.unit}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Reserved</div>
                    <div className="font-semibold text-lg text-orange-600">{(item.quantity - item.availableQuantity).toLocaleString()} {item.unit}</div>
                  </div>
                </div>
                <div className="mt-3">
                  <div className="flex justify-between text-sm mb-2">
                    <span>Availability</span>
                    <span className={((item.availableQuantity / item.quantity) * 100) < 20 ? 'text-red-600 font-semibold' : 'text-green-600 font-semibold'}>
                      {((item.availableQuantity / item.quantity) * 100).toFixed(1)}%
                    </span>
                  </div>
                  <Progress value={(item.availableQuantity / item.quantity) * 100} className="h-2" />
                </div>
              </div>

              {/* Pricing Section */}
              <div className="border-t pt-4">
                <h3 className="font-semibold mb-3">Pricing Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-gray-600">Price per Unit</div>
                    <div className="font-semibold">₹{item.pricePerUnit.toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Total Value</div>
                    <div className="font-semibold">₹{(item.quantity * item.pricePerUnit).toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Available Value</div>
                    <div className="font-semibold text-green-600">₹{(item.availableQuantity * item.pricePerUnit).toLocaleString()}</div>
                  </div>
                </div>
              </div>

              {/* Location & Compliance Section */}
              <div className="border-t pt-4">
                <h3 className="font-semibold mb-3">Location & Compliance</h3>
                <div className="space-y-3">
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-gray-400 mt-1" />
                    <div>
                      <div className="text-sm text-gray-600">Storage Location</div>
                      <div className="font-medium">{item.location.plantId} - {item.location.tankId}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Shield className="w-4 h-4 text-gray-400 mt-1" />
                    <div>
                      <div className="text-sm text-gray-600">PESO Compliance</div>
                      <div className="font-medium">
                        {item.pesoCompliance.status}
                        <span className="text-xs text-gray-500 ml-2">
                          (Expires: {new Date(item.pesoCompliance.expiryDate).toLocaleDateString()})
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Calendar className="w-4 h-4 text-gray-400 mt-1" />
                    <div>
                      <div className="text-sm text-gray-600">Production Date</div>
                      <div className="font-medium">{new Date(item.productionDate).toLocaleDateString()}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quality Grade Section */}
              <div className="border-t pt-4">
                <h3 className="font-semibold mb-3">Quality Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-gray-600">Quality Grade</div>
                    <Badge className="mt-1">{item.qualityGrade || 'Not Set'}</Badge>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Purity</div>
                    <div className="font-semibold">{item.purityPercentage || 'N/A'}%</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-2 pt-4 border-t">
              <Button variant="outline" className="flex-1" onClick={() => setShowViewDialog(false)}>
                Close
              </Button>
              <Button
                className="flex-1"
                onClick={() => {
                  setShowViewDialog(false);
                  onUpdateQuality?.(item);
                }}
              >
                <Shield className="w-4 h-4 mr-2" />
                Update Quality
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'available': return 'bg-green-100 text-green-800';
    case 'reserved': return 'bg-yellow-100 text-yellow-800';
    case 'sold': return 'bg-blue-100 text-blue-800';
    case 'expired': return 'bg-red-100 text-red-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const InventoryManagement: React.FC = () => {
  const navigate = useNavigate();
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [filteredInventory, setFilteredInventory] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<InventoryFilters>({});
  const [batchFilters, setBatchFilters] = useState<BatchFilters>({
    productType: 'ALL',
    qualityGrades: [],
    pesoCompliant: null,
    expiryRange: 'ALL',
    storageLocation: '',
    lowStockOnly: false
  });
  const [selectedBatchForUpdate, setSelectedBatchForUpdate] = useState<BatchResponse | null>(null);
  const [showUpdateQualityDialog, setShowUpdateQualityDialog] = useState(false);
  const [selectedBatchForPeso, setSelectedBatchForPeso] = useState<BatchResponse | null>(null);
  const [showFiltersPanel, setShowFiltersPanel] = useState(false);

  // US-BATCH-006: Expiring Batches State
  const [expiringBatches, setExpiringBatches] = useState<ExpiringBatch[]>([]);
  const [expiringBatchesSummary, setExpiringBatchesSummary] = useState<ExpiringBatchSummary | undefined>();
  const [expiringBatchesLoading, setExpiringBatchesLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('grid');
  const [showPesoDialog, setShowPesoDialog] = useState(false);

  useEffect(() => {
    const loadInventory = async () => {
      try {
        const response = await salesService.getInventory(filters);
        if (response.success) {
          setInventory(response.data);
          setFilteredInventory(response.data);
          // Load expiring batches when inventory loads
          loadExpiringBatches(response.data);
        }
      } catch (error) {
        console.error('Error loading inventory:', error);
      } finally {
        setLoading(false);
      }
    };
    loadInventory();
  }, [filters]);

  // Apply search and batch filters to inventory
  useEffect(() => {
    let filtered = [...inventory];

    // Apply search term filter
    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.batchNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.gasType.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.location.plantId.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply batch filters
    // Product type filter
    if (batchFilters.productType && batchFilters.productType !== 'ALL') {
      filtered = filtered.filter(item => {
        const itemType = item.gasType.toUpperCase();
        return itemType.includes(batchFilters.productType);
      });
    }

    // Quality grades filter
    if (batchFilters.qualityGrades && batchFilters.qualityGrades.length > 0) {
      filtered = filtered.filter(item =>
        item.qualityGrade && batchFilters.qualityGrades.includes(item.qualityGrade)
      );
    }

    // PESO compliance filter
    if (batchFilters.pesoCompliant !== null && batchFilters.pesoCompliant !== undefined) {
      filtered = filtered.filter(item =>
        (batchFilters.pesoCompliant ? item.pesoCompliance?.status === 'valid' : true)
      );
    }

    // Storage location filter
    if (batchFilters.storageLocation) {
      filtered = filtered.filter(item =>
        item.location.plantId.toLowerCase().includes(batchFilters.storageLocation.toLowerCase()) ||
        item.location.tankId.toLowerCase().includes(batchFilters.storageLocation.toLowerCase())
      );
    }

    // Low stock filter
    if (batchFilters.lowStockOnly) {
      filtered = filtered.filter(item => {
        const stockPercentage = (item.availableQuantity / item.quantity) * 100;
        return stockPercentage < 20;
      });
    }

    // Expiry range filter
    if (batchFilters.expiryRange && batchFilters.expiryRange !== 'ALL') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const thirtyDaysFromNow = new Date(today);
      thirtyDaysFromNow.setDate(today.getDate() + 30);
      const sixtyDaysFromNow = new Date(today);
      sixtyDaysFromNow.setDate(today.getDate() + 60);

      filtered = filtered.filter(item => {
        const expiryDate = new Date(item.expiryDate || item.pesoCompliance.expiryDate);
        expiryDate.setHours(0, 0, 0, 0);

        switch (batchFilters.expiryRange) {
          case 'EXPIRING_SOON':
            return expiryDate <= thirtyDaysFromNow;
          case '30-60':
            return expiryDate > thirtyDaysFromNow && expiryDate <= sixtyDaysFromNow;
          case 'OVER_60':
            return expiryDate > sixtyDaysFromNow;
          default:
            return true;
        }
      });
    }

    setFilteredInventory(filtered);
  }, [inventory, searchTerm, batchFilters]);

  /**
   * US-BATCH-006: Load expiring batches from inventory
   * Converts InventoryItem to ExpiringBatch format and calculates urgency
   */
  const loadExpiringBatches = (items: InventoryItem[]) => {
    setExpiringBatchesLoading(true);
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Filter and convert inventory items that are expiring within 30 days
      const expiring = items
        .map(item => {
          const expiryDate = new Date(item.expiryDate || item.pesoCompliance.expiryDate);
          expiryDate.setHours(0, 0, 0, 0);

          const daysRemaining = Math.floor(
            (expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
          );

          // Determine urgency level based on days remaining
          let urgencyLevel: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW' = 'LOW';
          if (daysRemaining < 0) urgencyLevel = 'CRITICAL'; // Expired
          else if (daysRemaining < 7) urgencyLevel = 'CRITICAL';
          else if (daysRemaining < 15) urgencyLevel = 'HIGH';
          else if (daysRemaining <= 30) urgencyLevel = 'MEDIUM';

          return {
            id: parseInt(item.id),
            batchNumber: item.batchNumber,
            productType: item.gasType,
            availableQuantity: item.availableQuantity,
            currentQuantity: item.quantity,
            quantityUnit: item.unit,
            qualityGrade: item.qualityGrade || 'A',
            purityPercentage: item.purityPercentage || 95,
            expiryDate: expiryDate.toISOString().split('T')[0],
            daysRemaining,
            urgencyLevel,
            storageLocation: `${item.location.plantId} - ${item.location.tankId}`,
            pesoCompliant: item.pesoCompliance?.status === 'valid',
            pesoCertificateNumber: item.pesoCompliance?.certificateNumber,
            productionDate: item.productionDate instanceof Date
              ? item.productionDate.toISOString().split('T')[0]
              : item.productionDate,
          } as ExpiringBatch;
        })
        .filter(batch => batch.daysRemaining <= 30) // Only batches expiring in next 30 days
        .sort((a, b) => a.daysRemaining - b.daysRemaining); // Sort by urgent first

      setExpiringBatches(expiring);

      // Calculate summary
      if (expiring.length > 0) {
        const summary: ExpiringBatchSummary = {
          totalExpiringCount: expiring.length,
          criticalCount: expiring.filter(b => b.daysRemaining < 7).length,
          warningCount: expiring.filter(b => b.daysRemaining >= 7 && b.daysRemaining < 15).length,
          normalCount: expiring.filter(b => b.daysRemaining >= 15 && b.daysRemaining <= 30).length,
          expiredCount: expiring.filter(b => b.daysRemaining < 0).length,
          mostUrgentBatch: expiring[0],
          actionRequired: expiring.some(b => b.daysRemaining < 7),
        };
        setExpiringBatchesSummary(summary);
      } else {
        setExpiringBatchesSummary(undefined);
      }
    } catch (error) {
      console.error('Error loading expiring batches:', error);
    } finally {
      setExpiringBatchesLoading(false);
    }
  };

  const getInventoryStats = () => {
    const totalItems = inventory.length;
    const totalValue = inventory.reduce((sum, item) => sum + (item.availableQuantity * item.pricePerUnit), 0);
    const lowStockItems = inventory.filter(item => (item.availableQuantity / item.quantity) < 0.2).length;
    const pesoExpiringItems = inventory.filter(item =>
      new Date(item.pesoCompliance.expiryDate) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
    ).length;

    return { totalItems, totalValue, lowStockItems, pesoExpiringItems };
  };

  const stats = getInventoryStats();

  // Helper function to count active filters
  const getActiveFilterCount = (): number => {
    let count = 0;
    if (batchFilters.productType && batchFilters.productType !== 'ALL') count++;
    if (batchFilters.qualityGrades && batchFilters.qualityGrades.length > 0) count += batchFilters.qualityGrades.length;
    if (batchFilters.pesoCompliant !== null && batchFilters.pesoCompliant !== undefined) count++;
    if (batchFilters.expiryRange && batchFilters.expiryRange !== 'ALL') count++;
    if (batchFilters.storageLocation) count++;
    if (batchFilters.lowStockOnly) count++;
    return count;
  };

  // Reset filters
  const handleResetFilters = () => {
    setBatchFilters({
      productType: 'ALL',
      qualityGrades: [],
      pesoCompliant: null,
      expiryRange: 'ALL',
      storageLocation: '',
      lowStockOnly: false
    });
  };

  // Handle filter changes
  const handleFiltersChange = (newFilters: BatchFilters) => {
    setBatchFilters(newFilters);
  };

  /**
   * Handle Update Quality action
   * US-BATCH-003: Convert InventoryItem to BatchResponse for dialog
   */
  const handleUpdateQuality = (item: InventoryItem) => {
    // Convert InventoryItem to BatchResponse format
    const batchResponse: BatchResponse = {
      id: parseInt(item.id),
      batchNumber: item.batchNumber,
      productType: item.gasType === 'compressed' ? 'COMPRESSED' : item.gasType === 'liquefied' ? 'LIQUIFIED' : 'BIO_CNG',
      productionDate: item.productionDate instanceof Date ? item.productionDate.toISOString().split('T')[0] : item.productionDate.toString(),
      expiryDate: item.expiryDate instanceof Date ? item.expiryDate.toISOString().split('T')[0] : item.expiryDate?.toString() || '',
      initialQuantity: item.quantity,
      currentQuantity: item.quantity,
      availableQuantity: item.availableQuantity,
      allocatedQuantity: item.quantity - item.availableQuantity,
      quantityUnit: item.unit === 'cubic_meters' ? 'CUBIC_METERS' : item.unit === 'kg' ? 'KG' : 'LITERS',
      qualityGrade: item.qualityGrade || 'A',
      purityPercentage: item.quality?.purity || item.purityPercentage || 95,
      pressure: item.quality?.pressure,
      storageLocation: item.location?.plantId || 'Unknown',
      batchStatus: item.status === 'available' ? 'AVAILABLE' : item.status === 'reserved' ? 'ALLOCATED' : 'SOLD',
      pesoCompliant: item.pesoCompliance?.status === 'valid',
      pesoCertificate: item.pesoCompliance ? {
        number: item.pesoCompliance.certificateNumber,
        inspector: item.pesoCompliance.inspector,
        issueDate: item.pesoCompliance.issueDate instanceof Date ? item.pesoCompliance.issueDate.toISOString() : item.pesoCompliance.issueDate.toString(),
        expiryDate: item.pesoCompliance.expiryDate instanceof Date ? item.pesoCompliance.expiryDate.toISOString() : item.pesoCompliance.expiryDate.toString(),
      } : undefined,
      createdBy: 'System',
      createdAt: new Date().toISOString(),
    };

    setSelectedBatchForUpdate(batchResponse);
    setShowUpdateQualityDialog(true);
  };

  /**
   * Handle Mark PESO Compliant action
   * US-BATCH-004: Convert InventoryItem to BatchResponse for dialog
   */
  const handleMarkPesoCompliant = (item: InventoryItem) => {
    // Convert InventoryItem to BatchResponse format
    const batchResponse: BatchResponse = {
      id: parseInt(item.id),
      batchNumber: item.batchNumber,
      productType: item.gasType === 'compressed' ? 'COMPRESSED' : item.gasType === 'liquefied' ? 'LIQUIFIED' : 'BIO_CNG',
      productionDate: item.productionDate instanceof Date ? item.productionDate.toISOString().split('T')[0] : item.productionDate.toString(),
      expiryDate: item.expiryDate instanceof Date ? item.expiryDate.toISOString().split('T')[0] : item.expiryDate?.toString() || '',
      initialQuantity: item.quantity,
      currentQuantity: item.quantity,
      availableQuantity: item.availableQuantity,
      allocatedQuantity: item.quantity - item.availableQuantity,
      quantityUnit: item.unit === 'cubic_meters' ? 'CUBIC_METERS' : item.unit === 'kg' ? 'KG' : 'LITERS',
      qualityGrade: item.qualityGrade || 'A',
      purityPercentage: item.quality?.purity || item.purityPercentage || 95,
      pressure: item.quality?.pressure,
      storageLocation: item.location?.plantId || 'Unknown',
      batchStatus: item.status === 'available' ? 'AVAILABLE' : item.status === 'reserved' ? 'ALLOCATED' : 'SOLD',
      pesoCompliant: item.pesoCompliance?.status === 'valid',
      pesoCertificate: item.pesoCompliance ? {
        number: item.pesoCompliance.certificateNumber,
        inspector: item.pesoCompliance.inspector,
        issueDate: item.pesoCompliance.issueDate instanceof Date ? item.pesoCompliance.issueDate.toISOString() : item.pesoCompliance.issueDate.toString(),
        expiryDate: item.pesoCompliance.expiryDate instanceof Date ? item.pesoCompliance.expiryDate.toISOString() : item.pesoCompliance.expiryDate.toString(),
      } : undefined,
      createdBy: 'System',
      createdAt: new Date().toISOString(),
    };

    setSelectedBatchForPeso(batchResponse);
    setShowPesoDialog(true);
  };

  /**
   * Handle Quality Update success
   * US-BATCH-003: Refresh inventory after updating quality
   */
  const handleQualityUpdateSuccess = async () => {
    // Reload inventory to get updated data
    try {
      const response = await salesService.getInventory(filters);
      if (response.success) {
        setInventory(response.data);
        setFilteredInventory(response.data);
      }
    } catch (error) {
      console.error('Error reloading inventory:', error);
    }
  };

  /**
   * Handle PESO compliance success
   * US-BATCH-004: Refresh inventory after marking PESO compliant
   */
  const handlePesoComplianceSuccess = async () => {
    // Reload inventory to get updated data
    try {
      const response = await salesService.getInventory(filters);
      if (response.success) {
        setInventory(response.data);
        setFilteredInventory(response.data);
      }
    } catch (error) {
      console.error('Error reloading inventory:', error);
    }
  };

  /**
   * US-BATCH-006: Handle expiring batch quick actions
   */
  const handleViewExpiringBatchDetails = (batch: ExpiringBatch) => {
    // Find corresponding inventory item and open details dialog
    const inventoryItem = inventory.find(item => item.batchNumber === batch.batchNumber);
    if (inventoryItem) {
      // You could open a detailed view dialog here
      console.log('View batch details:', batch.batchNumber);
      // TODO: Open batch details modal
    }
  };

  const handleCreateDiscountOrder = (batch: ExpiringBatch) => {
    console.log('Create discount order for batch:', batch.batchNumber);
    // TODO: Open discount order creation dialog
    // navigate(`/sales/create-order?batch=${batch.id}&discount=true`);
  };

  const handleMarkForDisposal = (batch: ExpiringBatch) => {
    console.log('Mark batch for disposal:', batch.batchNumber);
    // TODO: Open disposal confirmation dialog
    // This would update batch status to DISPOSED_PENDING or similar
  };

  const handleContactSalesTeam = (batch: ExpiringBatch) => {
    console.log('Contact sales team for batch:', batch.batchNumber);
    // TODO: Open contact form or send notification to sales team
    // This could open a modal to send a message to the sales team
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Inventory Management</h1>
          <p className="text-gray-600">Monitor gas inventory, batches, and PESO compliance</p>
        </div>
        <Button onClick={() => navigate('/inventory/batches/create')}>
          <Plus className="w-4 h-4 mr-2" />
          Add Batch
        </Button>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <Package className="w-8 h-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Items</p>
                <p className="text-2xl font-bold">{stats.totalItems}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <BarChart3 className="w-8 h-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Inventory Value</p>
                <p className="text-2xl font-bold">₹{(stats.totalValue / 1000000).toFixed(1)}M</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <AlertTriangle className="w-8 h-8 text-red-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Low Stock</p>
                <p className="text-2xl font-bold">{stats.lowStockItems}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <Shield className="w-8 h-8 text-orange-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">PESO Expiring</p>
                <p className="text-2xl font-bold">{stats.pesoExpiringItems}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by batch number, gas type, or location..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => setShowFiltersPanel(!showFiltersPanel)}
              className={getActiveFilterCount() > 0 ? 'border-blue-500 text-blue-600' : ''}
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
              {getActiveFilterCount() > 0 && (
                <Badge className="ml-2 bg-blue-500">{getActiveFilterCount()}</Badge>
              )}
            </Button>
          </div>

          {/* Filters Panel */}
          {showFiltersPanel && (
            <BatchFiltersPanel
              filters={batchFilters}
              onFiltersChange={handleFiltersChange}
              onReset={handleResetFilters}
              activeFilterCount={getActiveFilterCount()}
            />
          )}

          {/* Results count */}
          <div className="text-sm text-gray-600">
            Showing {filteredInventory.length} of {inventory.length} items
          </div>
        </CardContent>
      </Card>

      {/* US-BATCH-006: Expiring Batches Widget */}
      {expiringBatchesSummary && expiringBatchesSummary.totalExpiringCount > 0 && (
        <ExpiringBatchesWidget
          summary={expiringBatchesSummary}
          loading={expiringBatchesLoading}
          onViewDetails={() => {
            // Switch to expiring batches tab
            setActiveTab('expiring');
          }}
        />
      )}

      {/* Inventory Grid */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList>
          <TabsTrigger value="grid">Grid View</TabsTrigger>
          <TabsTrigger value="table">Table View</TabsTrigger>
          <TabsTrigger value="alerts">Alerts</TabsTrigger>
          <TabsTrigger value="expiring" className="flex items-center gap-2">
            Expiring Batches
            {expiringBatches.length > 0 && (
              <Badge className="ml-2 bg-red-600 text-white">{expiringBatches.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="grid" className="space-y-6">
          {loading ? (
            <div className="text-center py-8">Loading inventory...</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredInventory.map((item) => (
                <InventoryCard
                  key={item.id}
                  item={item}
                  onUpdateQuality={handleUpdateQuality}
                  onMarkPesoCompliant={handleMarkPesoCompliant}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="table">
          <Card>
            <CardHeader>
              <CardTitle>Inventory Table View</CardTitle>
              <CardDescription>Detailed inventory listing with sortable columns</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">Loading inventory...</div>
              ) : filteredInventory.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No inventory items found</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4 font-semibold">Batch #</th>
                        <th className="text-left py-3 px-4 font-semibold">Gas Type</th>
                        <th className="text-right py-3 px-4 font-semibold">Available</th>
                        <th className="text-right py-3 px-4 font-semibold">Total</th>
                        <th className="text-center py-3 px-4 font-semibold">Availability %</th>
                        <th className="text-left py-3 px-4 font-semibold">Location</th>
                        <th className="text-left py-3 px-4 font-semibold">Status</th>
                        <th className="text-center py-3 px-4 font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredInventory.map((item) => {
                        const availabilityPercent = (item.availableQuantity / item.quantity) * 100;
                        const isLowStock = availabilityPercent < 20;

                        return (
                          <tr key={item.id} className="border-b hover:bg-gray-50">
                            <td className="py-3 px-4 font-medium">{item.batchNumber}</td>
                            <td className="py-3 px-4">{item.gasType}</td>
                            <td className="py-3 px-4 text-right font-medium text-green-600">{item.availableQuantity.toLocaleString()} {item.unit}</td>
                            <td className="py-3 px-4 text-right font-medium">{item.quantity.toLocaleString()} {item.unit}</td>
                            <td className="py-3 px-4 text-center">
                              <Badge className={isLowStock ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}>
                                {availabilityPercent.toFixed(1)}%
                              </Badge>
                            </td>
                            <td className="py-3 px-4 text-sm">{item.location.plantId} - {item.location.tankId}</td>
                            <td className="py-3 px-4">
                              <Badge className={getStatusColor(item.status)}>{item.status}</Badge>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleUpdateQuality(item)}
                              >
                                Edit
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="alerts">
          <div className="space-y-4">
            {/* Critical Alerts */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <AlertTriangle className="w-5 h-5 mr-2 text-red-600" />
                  Critical Alerts (Low Stock)
                </CardTitle>
                <CardDescription>Batches with less than 10% availability</CardDescription>
              </CardHeader>
              <CardContent>
                {inventory.filter(item => (item.availableQuantity / item.quantity) < 0.1).length === 0 ? (
                  <div className="text-center py-6 text-gray-500">
                    <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-600" />
                    No critical alerts - all batches have sufficient stock
                  </div>
                ) : (
                  <div className="space-y-3">
                    {inventory.filter(item => (item.availableQuantity / item.quantity) < 0.1).map(item => {
                      const availabilityPercent = (item.availableQuantity / item.quantity) * 100;
                      return (
                        <div key={item.id} className="flex items-center justify-between p-4 border-l-4 border-l-red-600 rounded bg-red-50">
                          <div className="flex-1">
                            <div className="font-semibold text-gray-900">{item.batchNumber}</div>
                            <div className="text-sm text-gray-700 mt-1">
                              Available: <span className="font-medium text-red-600">{item.availableQuantity.toLocaleString()} {item.unit}</span> / Total: {item.quantity.toLocaleString()} {item.unit}
                            </div>
                            <div className="text-xs text-gray-600 mt-1">
                              Stock Level: <span className="font-semibold">{availabilityPercent.toFixed(1)}%</span> • Location: {item.location.plantId}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 ml-4">
                            <Badge className="bg-red-100 text-red-800 whitespace-nowrap">Critical</Badge>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleUpdateQuality(item)}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              View
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Warning Alerts */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <AlertTriangle className="w-5 h-5 mr-2 text-orange-600" />
                  Warning Alerts (Low to Medium Stock)
                </CardTitle>
                <CardDescription>Batches with 10-30% availability</CardDescription>
              </CardHeader>
              <CardContent>
                {inventory.filter(item => {
                  const percent = (item.availableQuantity / item.quantity) * 100;
                  return percent >= 0.1 && percent < 0.3;
                }).length === 0 ? (
                  <div className="text-center py-6 text-gray-500">
                    No warning alerts - all batches have good stock levels
                  </div>
                ) : (
                  <div className="space-y-3">
                    {inventory.filter(item => {
                      const percent = (item.availableQuantity / item.quantity) * 100;
                      return percent >= 0.1 && percent < 0.3;
                    }).map(item => {
                      const availabilityPercent = (item.availableQuantity / item.quantity) * 100;
                      return (
                        <div key={item.id} className="flex items-center justify-between p-4 border-l-4 border-l-orange-500 rounded bg-orange-50">
                          <div className="flex-1">
                            <div className="font-semibold text-gray-900">{item.batchNumber}</div>
                            <div className="text-sm text-gray-700 mt-1">
                              Available: <span className="font-medium text-orange-600">{item.availableQuantity.toLocaleString()} {item.unit}</span> / Total: {item.quantity.toLocaleString()} {item.unit}
                            </div>
                            <div className="text-xs text-gray-600 mt-1">
                              Stock Level: <span className="font-semibold">{availabilityPercent.toFixed(1)}%</span> • Location: {item.location.plantId}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 ml-4">
                            <Badge className="bg-orange-100 text-orange-800 whitespace-nowrap">Warning</Badge>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleUpdateQuality(item)}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              View
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Expiry Alerts */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Calendar className="w-5 h-5 mr-2 text-orange-600" />
                  Expiry Alerts (Within 30 Days)
                </CardTitle>
                <CardDescription>Batches expiring soon - action recommended</CardDescription>
              </CardHeader>
              <CardContent>
                {expiringBatches.length === 0 ? (
                  <div className="text-center py-6 text-gray-500">
                    <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-600" />
                    No batches expiring within 30 days
                  </div>
                ) : (
                  <div className="space-y-3">
                    {expiringBatches.map(batch => (
                      <div key={batch.id} className={`flex items-center justify-between p-4 rounded border-l-4 ${
                        batch.daysRemaining < 0
                          ? 'border-l-red-600 bg-red-50'
                          : batch.daysRemaining < 7
                          ? 'border-l-red-500 bg-red-50'
                          : 'border-l-orange-500 bg-orange-50'
                      }`}>
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900">{batch.batchNumber}</div>
                          <div className="text-sm text-gray-700 mt-1">
                            Expiry Date: <span className="font-medium">{new Date(batch.expiryDate).toLocaleDateString()}</span>
                          </div>
                          <div className="text-xs text-gray-600 mt-1">
                            Quality: {batch.qualityGrade} • Purity: {batch.purityPercentage.toFixed(1)}% • Available: {batch.availableQuantity.toLocaleString()} {batch.quantityUnit}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <Badge className={
                            batch.daysRemaining < 0
                              ? 'bg-red-100 text-red-800'
                              : batch.daysRemaining < 7
                              ? 'bg-red-100 text-red-800'
                              : 'bg-orange-100 text-orange-800'
                          }>
                            {batch.daysRemaining < 0 ? 'Expired' : `${batch.daysRemaining}d left`}
                          </Badge>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              const inventoryItem = inventory.find(item => item.batchNumber === batch.batchNumber);
                              if (inventoryItem) {
                                handleUpdateQuality(inventoryItem);
                              }
                            }}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* US-BATCH-006: Expiring Batches Tab */}
        <TabsContent value="expiring">
          <ExpiringBatchesAlertTab
            batches={expiringBatches}
            loading={expiringBatchesLoading}
            onViewDetails={handleViewExpiringBatchDetails}
            onCreateDiscountOrder={handleCreateDiscountOrder}
            onMarkForDisposal={handleMarkForDisposal}
            onContactSales={handleContactSalesTeam}
          />
        </TabsContent>
      </Tabs>

      {/* US-BATCH-003: Update Quality Dialog */}
      {selectedBatchForUpdate && (
        <UpdateQualityDialog
          batch={selectedBatchForUpdate}
          open={showUpdateQualityDialog}
          onOpenChange={setShowUpdateQualityDialog}
          onSuccess={handleQualityUpdateSuccess}
        />
      )}

      {/* US-BATCH-004: PESO Compliance Dialog */}
      {selectedBatchForPeso && (
        <PesoComplianceDialog
          batch={selectedBatchForPeso}
          open={showPesoDialog}
          onOpenChange={setShowPesoDialog}
          onSuccess={handlePesoComplianceSuccess}
        />
      )}
    </div>
  );
};

export default InventoryManagement;